import { useEffect, useState } from 'react';
import { X, Plus, Minus, ShoppingBag } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCartStore } from '@/stores/cartStore';
import { useCurrencyStore } from '@/stores/currencyStore';
import { getProducts } from '@/lib/api';
import type { Product } from '@/types';

export default function CartDrawer() {
  const { isOpen, closeCart, items, removeItem, updateQuantity } = useCartStore();
  const { format } = useCurrencyStore();
  const [products, setProducts] = useState<Product[]>([]);

  useEffect(() => {
    getProducts().then((data) => setProducts(data));
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const lookup = (id: string) => products.find((p) => p.id === id);
  const total = items.reduce((sum, item) => {
    const p = lookup(item.productId);
    return sum + (p ? p.priceUsd * item.quantity : 0);
  }, 0);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeCart}
            className="fixed inset-0 z-[90] bg-rp-ink/40"
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 z-[100] flex h-full w-[min(420px,90vw)] flex-col bg-rp-paper shadow-panel"
          >
            <div className="flex items-center justify-between border-b border-rp-line px-6 py-4">
              <h2 className="flex items-center gap-2 text-lg font-semibold text-rp-forest">
                <ShoppingBag size={20} />
                Your cart
              </h2>
              <button onClick={closeCart} className="flex h-9 w-9 items-center justify-center rounded-full border border-rp-line text-rp-forest">
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {items.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center text-center text-rp-moss">
                  <ShoppingBag size={48} className="mb-4 opacity-30" />
                  <p className="text-lg font-medium text-rp-ink">Your cart is empty</p>
                  <p className="mt-1 text-sm">Add a product to get started.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {items.map((item) => {
                    const p = lookup(item.productId);
                    if (!p) return null;
                    return (
                      <div key={item.productId} className="flex gap-4 rounded-xl border border-rp-line bg-rp-bone p-3">
                        <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-lg bg-rp-paper">
                          {p.image ? (
                            <img src={p.image} alt={p.name} className="h-full w-full object-contain p-1" />
                          ) : (
                            <svg viewBox="0 0 100 100" fill="none" className="h-10 w-10 opacity-60">
                              <rect x="35" y="20" width="30" height="60" rx="15" fill="#f4f1ea" stroke="#0abab5" strokeWidth="2" />
                              <rect x="42" y="30" width="16" height="36" rx="8" fill="#4dd4cf" fillOpacity="0.35" />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-rp-forest">{p.name}</p>
                          <p className="text-xs text-rp-moss">{p.dose}</p>
                          <p className="mt-1 text-sm font-semibold">{format(p.priceUsd)}</p>
                        </div>
                        <div className="flex flex-col items-end justify-between">
                          <button onClick={() => removeItem(item.productId)} className="text-xs text-rp-warn underline">Remove</button>
                          <div className="flex items-center gap-2 rounded-full border border-rp-line bg-rp-paper px-2 py-1">
                            <button onClick={() => updateQuantity(item.productId, item.quantity - 1)} className="text-rp-forest">
                              <Minus size={14} />
                            </button>
                            <span className="min-w-[1ch] text-center text-xs font-bold">{item.quantity}</span>
                            <button onClick={() => updateQuantity(item.productId, item.quantity + 1)} className="text-rp-forest">
                              <Plus size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-rp-line p-6">
                <div className="flex items-center justify-between text-rp-ink">
                  <span className="font-medium">Subtotal</span>
                  <span className="text-lg font-bold text-rp-forest">{format(total)}</span>
                </div>
                <p className="mt-1 text-xs text-rp-moss">Shipping & taxes calculated at checkout.</p>
                <button className="rp-btn-primary mt-4 w-full">Checkout on WhatsApp</button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
